import {useMutation, useQueryClient} from '@tanstack/react-query';

import apiService from '../../transport/apiService';
import {OrderLine} from '../../transport/apiServiceResponse';
import {logger, serializeError} from '../../utils/logger';

import {wishlistKeys} from './keys';
import {WishlistData} from './types';

/**
 * Mutation hook for removing an item from the wishlist
 *
 * Finds the entry by product_code or product_id, removes it from the wishlist,
 * and invalidates the cache to refetch the updated entries list.
 *
 * @returns Mutation object with mutate function and loading state
 *
 * @example
 * const {mutate: removeFromWishlist, isPending} = useRemoveFromWishlist();
 * removeFromWishlist(orderLine);
 */
export const useRemoveFromWishlist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (item: OrderLine) => {
      const wishlistData = queryClient.getQueryData<WishlistData>(wishlistKeys.list());

      if (!wishlistData?.wishlist.id) {
        throw new Error(
          'Unable to remove from wishlist: no wishlist loaded. User may need to reload the page.',
        );
      }

      // Find the entry to get its ID (required for DELETE)
      const entry = wishlistData.entries.find(
        e => e.product_code === item.product_code || e.product_id === item.product_id,
      );

      if (!entry) {
        // Entry not found - may be out of sync, just invalidate cache
        logger.warn('useRemoveFromWishlist', 'Entry not found in cache, invalidating', {
          productCode: item.product_code,
        });
        await queryClient.invalidateQueries({queryKey: wishlistKeys.list()});
        return item;
      }

      await apiService.removeItemFromWishlist(
        wishlistData.wishlist.id,
        entry.id,
        entry.product_code,
      );

      logger.info('useRemoveFromWishlist', 'Item removed from wishlist', {
        productCode: item.product_code,
      });

      return item;
    },

    onSuccess: async () => {
      // Refetch entries to get the updated list from server
      await queryClient.refetchQueries({queryKey: wishlistKeys.list()});
      // Products will auto-refetch due to changed entry IDs in query key
    },

    onError: (error, item) => {
      logger.error('useRemoveFromWishlist', 'Failed to remove item from wishlist', {
        productId: item.product_id,
        productCode: item.product_code,
        productName: item.name,
        ...serializeError(error),
      });
    },
  });
};
